/**
 * Pagination Component - « ‹ 1 … 4 5 [6] 7 8 … 20 › »
 *
 *   <Pagination
 *     current={page}
 *     total={totalPages}
 *     onChange={setPage}
 *     info="1–5 / 12개"      // 선택: 오른쪽 보조 텍스트
 *   />
 *
 * - 번호 노출: 처음 · 끝 · 현재 페이지 앞뒤 2개, 사이가 비면 … 표시
 * - 현재 페이지 상태 / 페이지 크기 / 목록 자르기는 사용하는 페이지에서 관리
 * - 스타일: index.css "Pagination (공통)"
 */

import { ReactNode } from 'react';
import FirstIcon from '../../assets/icons/common/btn-first.svg?react';
import PrevIcon from '../../assets/icons/common/btn-before.svg?react';
import NextIcon from '../../assets/icons/common/btn-next.svg?react';
import LastIcon from '../../assets/icons/common/btn-last.svg?react';

interface PaginationProps {
  current: number;
  total: number;
  onChange: (page: number) => void;
  info?: ReactNode;
  className?: string;
}

export function Pagination({ current, total, onChange, info, className = '' }: PaginationProps) {
  const pages = Array.from({ length: total }, (_, i) => i + 1)
    .filter(p => p === 1 || p === total || Math.abs(p - current) <= 2)
    .reduce<(number | '...')[]>((acc, p, idx, arr) => {
      if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className={`pagination ${className}`.trim()}>
      <button type="button" className="pagination-btn pagination-nav" onClick={() => onChange(1)} disabled={current === 1} title="첫 페이지" aria-label="첫 페이지"><FirstIcon /></button>
      <button type="button" className="pagination-btn pagination-nav" onClick={() => onChange(Math.max(1, current - 1))} disabled={current === 1} aria-label="이전 페이지"><PrevIcon /></button>
      {pages.map((p, idx) =>
        p === '...' ? (
          <span key={`e-${idx}`} className="pagination-ellipsis">…</span>
        ) : (
          <button type="button" key={p} className={`pagination-btn ${current === p ? 'active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        )
      )}
      <button type="button" className="pagination-btn pagination-nav" onClick={() => onChange(Math.min(total, current + 1))} disabled={current === total} aria-label="다음 페이지"><NextIcon /></button>
      <button type="button" className="pagination-btn pagination-nav" onClick={() => onChange(total)} disabled={current === total} title="마지막 페이지" aria-label="마지막 페이지"><LastIcon /></button>
      {info && <span className="pagination-info">{info}</span>}
    </div>
  );
}

export default Pagination;
